/**
 * Who may do what on one programme, as a grid of people against permissions.
 *
 * A table, or a card per person, from one cell description so the two cannot
 * disagree about what is checked or why something is refused.
 *
 * @module components/teams/ProgrammePermissions
 */

import React from "react";

import { roleLabel } from "@/components/shared/role-labels";
import type { ResponsibilityGroup } from "@/components/teams/team-assignments";
import {
  Badge,
  Checkbox,
  CollectionView,
  type Column,
  EmptyState,
  MutedText,
  Panel,
  useConfirm,
} from "@/components/ui";
import type { ProgrammeResponsibility } from "@/server/types";

export interface PermissionOption {
  value: ProgrammeResponsibility;
  label: string;
}

/** One cell's state, decided once for both renderings. */
export interface PermissionCell {
  checked: boolean;
  disabled: boolean;
  /** Why, in a sentence. Also the checkbox's pointer title. */
  reason: string;
}

export interface ProgrammePermissionsProps {
  groups: readonly ResponsibilityGroup[];
  options: readonly PermissionOption[];
  cellFor: (group: ResponsibilityGroup, option: PermissionOption) => PermissionCell;
  onToggle: (group: ResponsibilityGroup, option: PermissionOption) => void;
  /** The staff picker and its Add control, which the container owns. */
  addStaff: React.ReactNode;
}

const EMPTY = "No programme responsibilities assigned.";

export function ProgrammePermissions({
  groups,
  options,
  cellFor,
  onToggle,
  addStaff,
}: ProgrammePermissionsProps): React.ReactElement {
  const confirm = useConfirm();
  // Revoking a builder also unassigns every module they build, so it is asked.
  const toggle = (group: ResponsibilityGroup, option: PermissionOption, checked: boolean): void => {
    if (!checked || option.value !== "builder") {
      onToggle(group, option);
      return;
    }
    confirm({
      title: `Remove ${group.displayName} as a builder?`,
      body: "Any modules they build on this programme become unassigned.",
      confirmLabel: "Remove builder",
      tone: "danger",
      "data-testid": "builder-revoke-confirmation",
    })
      .then((confirmed) => {
        if (confirmed) {
          onToggle(group, option);
        }
      })
      .catch(() => undefined);
  };

  /** One box, drawn the same in the table and in a person's card. */
  const box = (group: ResponsibilityGroup, option: PermissionOption): React.ReactElement => {
    const cell = cellFor(group, option);
    // A refusal is a tooltip on focus and the box's description. A `title` is
    // not announced reliably and never shows on focus or touch.
    return (
      <Checkbox
        size="sm"
        className="shrink-0"
        checked={cell.checked}
        disabled={cell.disabled}
        disabledReason={cell.reason}
        title={cell.disabled ? undefined : cell.reason}
        aria-label={`${option.label} permission for ${group.displayName}`}
        onChange={() => {
          toggle(group, option, cell.checked);
        }}
        data-testid={`programme-permission-${group.userId}-${option.value}`}
      />
    );
  };

  const columns: Column<ResponsibilityGroup>[] = [
    {
      key: "user",
      header: "User",
      render: (group) => (
        <>
          <div className="font-medium">{group.displayName}</div>
          <MutedText size="caption" as="div">
            {roleLabel(group.accountRole)}
          </MutedText>
        </>
      ),
    },
    ...options.map<Column<ResponsibilityGroup>>((option) => ({
      key: option.value,
      header: option.label,
      render: (group) => box(group, option),
    })),
  ];

  return (
    <Panel
      attached
      icon="users-three"
      aria-label="Programme responsibilities"
      className="min-w-0 max-w-full overflow-hidden"
      // The action beside the count it changes. The tab above already names
      // the section, so the panel carries no title of its own.
      actions={
        <>
          <Badge
            tone="neutral"
            variant="outline"
            size="sm"
            aria-label={`${groups.length} ${groups.length === 1 ? "person" : "people"} with responsibilities`}
          >
            {groups.length}
          </Badge>
          {addStaff}
        </>
      }
    >
      <div data-testid="assignment-management-responsibilities">
        <MutedText className="mb-3">
          Check or clear the permissions each person needs for this programme.
        </MutedText>
        <CollectionView
          storageKey="programme-permissions"
          caption="Programme permissions by person"
          columns={columns}
          rows={groups}
          rowKey={(group) => group.userId}
          density="sm"
          emptyState={<EmptyState icon="users-three" title={EMPTY} density="row" />}
          data-testid="programme-responsibilities-table"
        />
      </div>
    </Panel>
  );
}
